import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';

import { useCompanyData } from '~/data/company';
import { Enum, EnumListKey, useAllEnums } from '~/data/enums';
import {
  PersonnelGroupFilterIndex,
  PersonnelGroupMappedData,
} from '~/scenes/PortalModules/Analytics/People/AbsenceCalendarReport/data';
import { getLargerNumber } from '~/utils/helper';

type UsePersonnelGroupFilterOutput = {
  label: string;
  options: Enum[];
  value: string;
  setValue: Dispatch<SetStateAction<string>>;
  fieldWidth: number;
};
type PersonnelGroupEnumListKey =
  | EnumListKey.PERSONNEL_GROUP_1
  | EnumListKey.PERSONNEL_GROUP_2
  | EnumListKey.PERSONNEL_GROUP_3
  | EnumListKey.PERSONNEL_GROUP_4
  | EnumListKey.PERSONNEL_GROUP_5;

type UsePersonnelGroupFilterProps = {
  group: PersonnelGroupEnumListKey;
  personnelGroupNames: Enum[];
  personnelGroupEnumsByGroup: {
    [p: string]: Enum[];
  };
  isOptionDisabled: (option: Enum) => boolean;
};

const usePersonnelGroupFilter = (
  props: UsePersonnelGroupFilterProps,
): UsePersonnelGroupFilterOutput => {
  const {
    group,
    personnelGroupNames,
    personnelGroupEnumsByGroup,
    isOptionDisabled,
  } = props;

  const { label, options, fieldWidth } = useMemo(() => {
    const options = (personnelGroupEnumsByGroup[group] ?? []).filter((e) => {
      return !(
        typeof e.isActive === 'boolean' &&
        !e.isActive &&
        isOptionDisabled(e)
      );
    });

    const characterSize = 10;
    const dropDownBuffer = 30;
    const characterCount = options.reduce(
      (width, { name }) => getLargerNumber(width, name.length),
      0,
    );
    const fieldWidth = characterCount * characterSize + dropDownBuffer;

    return {
      label: personnelGroupNames.find(({ code }) => code === group)?.name ?? '',
      options,
      fieldWidth,
    };
  }, [
    personnelGroupNames,
    personnelGroupEnumsByGroup,
    group,
    isOptionDisabled,
  ]);

  const [value, setValue] = useState<string>('');

  return {
    label,
    options,
    value,
    setValue,
    fieldWidth,
  };
};

export type UsePersonnelGroupsFiltersOutput = {
  enabledPersonnelGroupFilters: PersonnelGroupFilterIndex[];
  filters: {
    [key in PersonnelGroupFilterIndex]: UsePersonnelGroupFilterOutput;
  };
  checkPersonnelGroupFilteringValue: (
    pgIndex: PersonnelGroupFilterIndex,
    compareValue?: string,
  ) => boolean;
};

type PropsType = {
  isPersonnelGroupOptionDisabled: (
    group: PersonnelGroupFilterIndex,
    option: Enum,
  ) => boolean;
};

export const usePersonnelGroupsFilters = (
  props: PropsType,
): UsePersonnelGroupsFiltersOutput => {
  const { isPersonnelGroupOptionDisabled } = props;

  const { getEnums } = useAllEnums();

  const { companyData } = useCompanyData();

  const enabledPersonnelGroupFilters = useMemo(() => {
    return Object.values(PersonnelGroupFilterIndex).slice(
      0,
      companyData.config.fComMaxPersonnelGroups ?? 0,
    );
  }, [companyData]);

  const enums = useMemo(() => {
    return {
      personnelGroupNames: getEnums(EnumListKey.PERSONNEL_GROUP_NAME),
      personnelGroupEnumsByGroup: Object.fromEntries(
        [
          EnumListKey.PERSONNEL_GROUP_1,
          EnumListKey.PERSONNEL_GROUP_2,
          EnumListKey.PERSONNEL_GROUP_3,
          EnumListKey.PERSONNEL_GROUP_4,
          EnumListKey.PERSONNEL_GROUP_5,
        ].map((key) => {
          return [key, getEnums(key)];
        }),
      ),
    };
  }, [getEnums]);

  const getGroupFilterProps = useCallback(
    (
      group: PersonnelGroupEnumListKey,
      filterIndex: PersonnelGroupFilterIndex,
    ): UsePersonnelGroupFilterProps => {
      return {
        group,
        ...enums,
        isOptionDisabled: (option: Enum) =>
          isPersonnelGroupOptionDisabled(filterIndex, option),
      };
    },
    [enums, isPersonnelGroupOptionDisabled],
  );

  const personnelGroup1Filter = usePersonnelGroupFilter(
    getGroupFilterProps(
      EnumListKey.PERSONNEL_GROUP_1,
      PersonnelGroupFilterIndex.PG1,
    ),
  );
  const personnelGroup2Filter = usePersonnelGroupFilter(
    getGroupFilterProps(
      EnumListKey.PERSONNEL_GROUP_2,
      PersonnelGroupFilterIndex.PG2,
    ),
  );
  const personnelGroup3Filter = usePersonnelGroupFilter(
    getGroupFilterProps(
      EnumListKey.PERSONNEL_GROUP_3,
      PersonnelGroupFilterIndex.PG3,
    ),
  );
  const personnelGroup4Filter = usePersonnelGroupFilter(
    getGroupFilterProps(
      EnumListKey.PERSONNEL_GROUP_4,
      PersonnelGroupFilterIndex.PG4,
    ),
  );
  const personnelGroup5Filter = usePersonnelGroupFilter(
    getGroupFilterProps(
      EnumListKey.PERSONNEL_GROUP_5,
      PersonnelGroupFilterIndex.PG5,
    ),
  );

  const personnelGroupFilters =
    useMemo((): PersonnelGroupMappedData<UsePersonnelGroupFilterOutput> => {
      return {
        [PersonnelGroupFilterIndex.PG1]: personnelGroup1Filter,
        [PersonnelGroupFilterIndex.PG2]: personnelGroup2Filter,
        [PersonnelGroupFilterIndex.PG3]: personnelGroup3Filter,
        [PersonnelGroupFilterIndex.PG4]: personnelGroup4Filter,
        [PersonnelGroupFilterIndex.PG5]: personnelGroup5Filter,
      };
    }, [
      personnelGroup1Filter,
      personnelGroup2Filter,
      personnelGroup3Filter,
      personnelGroup4Filter,
      personnelGroup5Filter,
    ]);

  const checkPersonnelGroupFilteringValue = useCallback(
    (pgIndex: PersonnelGroupFilterIndex, compareValue?: string) => {
      const isEnabled = enabledPersonnelGroupFilters.includes(pgIndex);
      const filterValue = personnelGroupFilters[pgIndex].value;

      if (isEnabled && filterValue !== '' && !!compareValue) {
        return filterValue === compareValue;
      }

      return true;
    },
    [enabledPersonnelGroupFilters, personnelGroupFilters],
  );

  return {
    checkPersonnelGroupFilteringValue,
    enabledPersonnelGroupFilters,
    filters: personnelGroupFilters,
  };
};
